import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import { type OwnerWorkspaceFilters } from 'config/api/assetRopa/assetRopaFilter.types';
import { ownerWorkspaceFiltersToQueryParams } from 'config/api/assetRopa/assetRopaFilterMappers';
import { DEFAULT_ASSET_ROPA_OWNER_PAGE_SIZE } from 'config/api/assetRopa/assetRopaOwnerFilter.types';
import invalidateOwnerAssetRopaQueries from 'config/api/assetRopa/assetRopaQuery.helpers';
import { buildOwnerWorkspaceFilterOptions } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import type { AddProcessingActivityInput, SoftDeleteProcessingActivityInput } from 'config/api/assetRopa/assetRopaWorkspace.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import { catchHttpStatuses, isUnavailableQueryResult } from 'pages/ProcessingActivities/_shared/queryUnavailable';

type UseOwnerAssetRopaWorkspaceOptions = {
  filters: OwnerWorkspaceFilters;
  organizationId?: number;
};

export const OWNER_WORKSPACE_SESSION_FILTERS_KEY = 'OWNER_ASSET_ROPA_WORKSPACE_FILTERS';

/**
 * Owner workspace is scoped via asset-ropa/mine* only — never admin review assets list.
 */
const useOwnerAssetRopaWorkspace = ({ filters, organizationId }: UseOwnerAssetRopaWorkspaceOptions) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_ASSET_ROPA_OWNER_PAGE_SIZE;

  useEffect(() => {
    setPage(1);
  }, [filters, organizationId]);

  const filterParams = ownerWorkspaceFiltersToQueryParams(filters, { page, pageSize });

  const workspaceQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId, filterParams],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await assetRopaApi.getOwnerWorkspace(organizationId as number, filterParams);
        return data;
      }, [403]),
    {
      enabled: Boolean(organizationId),
      retry: false,
      keepPreviousData: true,
    },
  );

  const metricsQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId],
    async () => {
      const { data } = await assetRopaApi.getMineMetrics(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
      keepPreviousData: true,
    },
  );

  const workspaceData = isUnavailableQueryResult(workspaceQuery.data) ? undefined : workspaceQuery.data;
  const assets = workspaceData?.assets ?? [];
  const count = workspaceData?.count ?? assets.length;
  const hasNextPage = page * pageSize < count;
  const hasPreviousPage = page > 1;

  const filterOptions = useMemo(() => buildOwnerWorkspaceFilterOptions(assets), [assets]);

  const workspaceMetrics = metricsQuery.data?.workspaceMetrics ?? workspaceData?.workspaceMetrics;
  const reviewMetrics = metricsQuery.data !== undefined ? metricsQuery.data.reviewMetrics : workspaceData?.reviewMetrics ?? null;

  const invalidateWorkspace = async () => {
    await invalidateOwnerAssetRopaQueries(queryClient, organizationId);
  };

  const addProcessingActivityMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_ADD_PROCESSING_ACTIVITY,
    async ({ assetRopaId, payload }: { assetRopaId: number; payload: AddProcessingActivityInput }) => {
      const { data } = await assetRopaApi.addProcessingActivity(organizationId as number, assetRopaId, payload);
      return data;
    },
    { onSuccess: invalidateWorkspace },
  );

  const softDeleteMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_SOFT_DELETE_APA,
    async ({ apaId, assetRopaId, payload }: { apaId: number; assetRopaId: number; payload: SoftDeleteProcessingActivityInput }) => {
      const { data } = await assetRopaApi.softDeleteProcessingActivity(organizationId as number, assetRopaId, apaId, payload);
      return data;
    },
    { onSuccess: invalidateWorkspace },
  );

  const forbiddenStatus = isUnavailableQueryResult(workspaceQuery.data) ? workspaceQuery.data.status : undefined;
  const isForbidden = forbiddenStatus === 403;
  const { isLoading } = workspaceQuery;
  const { isError } = workspaceQuery;

  return {
    activeReview: workspaceData?.activeReview ?? null,
    addProcessingActivityMutation,
    assets,
    count,
    filterOptions,
    hasNextPage,
    hasPreviousPage,
    invalidateWorkspace,
    isError,
    isForbidden,
    isLoading,
    page,
    pageSize,
    refetch: async () => {
      await Promise.all([workspaceQuery.refetch(), metricsQuery.refetch()]);
    },
    reviewMetrics,
    setPage,
    softDeleteMutation,
    workspaceMetrics,
  };
};

export default useOwnerAssetRopaWorkspace;
